import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';

export interface CreateFolderDialogProps {
  open: boolean;
  currentPath: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (folderName: string) => void;
}

function validate(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'Folder name is required.';
  if (trimmed.includes('/')) return 'Create one folder at a time.';
  if (trimmed.startsWith('.')) return 'Names cannot start with a dot.';
  return '';
}

export function CreateFolderDialog({
  open, currentPath, busy, onCancel, onConfirm,
}: CreateFolderDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName('');
      setError('');
    }
  }, [open]);

  const handleConfirm = () => {
    const trimmed = name.trim();
    const validationErr = validate(trimmed);
    if (validationErr) {
      setError(validationErr);
      return;
    }
    onConfirm(trimmed);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>New folder</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          autoFocus
          fullWidth
          label="Folder name"
          value={name}
          onChange={(e) => { setName(e.target.value); setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); } }}
          disabled={busy}
          helperText={currentPath ? `Will be created in: /${currentPath}` : 'Will be created in the root folder'}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={handleConfirm} disabled={busy} variant="contained">
          {busy ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
